import React, { createContext, useContext, useState } from 'react';
import { getBaseCode, setLanguage as setStoreLanguage } from '../i18n/languages';
import { resolveEnglishText, useLiveLanguage } from '../i18n/uiStrings';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// ── Migrate old kisanai_ localStorage keys → fassalkartai_ ─────────────────
// Language is NOT in this list any more: src/i18n/languages.js owns the
// language key and migrates 'fassalkartai_lang' / 'kisanai_lang' itself.
const migrateStorage = () => {
  const pairs = [['kisanai_district', 'fassalkartai_district'], ['kisanai_state', 'fassalkartai_state']];
  pairs.forEach(([oldKey, newKey]) => {
    if (!localStorage.getItem(newKey) && localStorage.getItem(oldKey)) {
      localStorage.setItem(newKey, localStorage.getItem(oldKey));
      localStorage.removeItem(oldKey);
    }
  });
};
migrateStorage();

export const AppProvider = ({ children }) => {
  // Derived view of the ONE authoritative language store
  // (src/i18n/languages.js). AppProvider is mounted outside LanguageProvider,
  // so it cannot read that context — it subscribes to the shared store
  // instead. Picking Tamil in the navbar now updates this value in the same
  // tick, which is what un-freezes the `hi ? … : …` ternaries downstream.
  const selectedLanguage = useLiveLanguage();

  const [selectedDistrict, setSelectedDistrict] = useState(
    () => localStorage.getItem('fassalkartai_district') || ''
  );
  const [selectedState, setSelectedState] = useState(
    () => localStorage.getItem('fassalkartai_state') || ''
  );
  const [farmerProfile, setFarmerProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Writes go to the shared store, which persists to localStorage and
  // notifies LanguageContext too — so Login's language chips and the navbar
  // picker can never disagree again.
  const setLanguage = (lang) => {
    setStoreLanguage(lang);
  };

  const setDistrict = (district) => {
    setSelectedDistrict(district);
    localStorage.setItem('fassalkartai_district', district);
  };

  const setState = (state) => {
    setSelectedState(state);
    localStorage.setItem('fassalkartai_state', state);
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
  };

  const markAllRead = () => {
    setUnreadCount(0);
  };

  /**
   * Legacy two-language helper, kept because 251 call sites across 5 pages
   * use it. The signature CANNOT express more than two languages, so it is
   * resolved against the real selection like this:
   *
   *   base 'hi'  (hi, hi-en)  -> hiText
   *   base 'en'  (en)         -> enText
   *   anything else (ta, bn…) -> if `enText` is a literal that exists in the
   *                              UI-string catalogue, return that catalogue
   *                              entry in the selected language; otherwise
   *                              return enText.
   *
   * The point of the last rule: a Tamil user must never be shown Hindi. Where
   * a real Tamil string exists we use it, and English — which most Indian
   * farmers can partially read, and which every screen reader handles — is the
   * fallback, not Devanagari the user cannot read at all.
   *
   * New code should use `useLanguage().t` / `useUiString()` instead, which
   * cover all 16 languages properly.
   *
   * @param {string} hiText Hindi literal
   * @param {string} enText English literal
   * @returns {string}
   */
  const t = (hiText, enText) => {
    const base = getBaseCode(selectedLanguage);
    if (base === 'hi') return hiText;
    if (base === 'en') return enText !== undefined ? enText : hiText;

    const fromCatalogue = resolveEnglishText(enText, selectedLanguage);
    if (fromCatalogue) return fromCatalogue;

    return enText !== undefined ? enText : hiText;
  };

  // GPS: detect current location via browser geolocation + Nominatim reverse geocode
  const detectLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
              { headers: { 'Accept-Language': 'en-IN' } }
            );
            const data = await res.json();
            const addr = data.address || {};
            // Nominatim returns state and county/district for India
            const state    = addr.state || '';
            const district = addr.county || addr.state_district || addr.district || addr.city_district || '';
            if (state)    { setSelectedState(state);       localStorage.setItem('fassalkartai_state', state); }
            if (district) { setSelectedDistrict(district); localStorage.setItem('fassalkartai_district', district); }
            resolve({ state, district, latitude, longitude });
          } catch (err) {
            reject(err);
          }
        },
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  };

  const value = {
    selectedLanguage,
    setLanguage,
    selectedDistrict,
    setDistrict,
    selectedState,
    setState,
    detectLocation,
    farmerProfile,
    setFarmerProfile,
    notifications,
    addNotification,
    unreadCount,
    markAllRead,
    t
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
